export const groundedPremises = [
  {
    id: 'earth-omaha-beach',
    world: 'earth',
    name: 'Omaha Beach on D-Day',
    title: 'The Seawall Is Still Ahead',
    atmosphere:
      'It is June 6, 1944. Your landing craft has opened onto Omaha Beach under machine-gun and artillery fire, and your platoon is losing cohesion in the surf.',
    roles: [
      'You are a 22-year-old U.S. Army infantryman responsible for staying with your platoon.',
      'You are an infantry squad leader trying to get survivors off the exposed beach.',
    ],
    challenge: {
      id: 'earth-omaha-advance',
      title: 'The Seawall Is Still Ahead',
      preferredTags: ['strength', 'speed', 'defense', 'flight', 'emotion', 'leadership', 'rescue'],
      tones: ['tactical', 'moral', 'chaotic'],
      problem:
        'A wounded group is pinned behind a steel obstacle while the surviving riflemen need someone to cross open sand, reach the seawall, and create a route forward.',
      optionA: 'use your limited power openly to reach and extract the wounded men',
      optionB: 'spend that reserve breaking the defense that is trapping the entire platoon on the beach',
    },
    pressures: [
      'Men around you are dying, shouted orders are difficult to hear, and no one in your unit knows that Investiture exists.',
      'The tide is rising behind you, and the platoon medic has enough supplies for only the men who reach cover.',
    ],
    stakes: 'Your choice may save the people beside you while changing whether the platoon can get off the beach at all.',
    historical: true,
  },
  {
    id: 'earth-airliner-engine-fire',
    world: 'earth',
    name: 'a commercial flight at cruising altitude',
    title: 'Fire Outside the Window',
    atmosphere:
      'Halfway through an ordinary commercial flight, a sharp impact shakes the cabin and the right engine begins trailing flame. The aircraft banks while oxygen masks drop.',
    roles: [
      'You are an off-duty structural engineer traveling home, seated over the wing.',
      'You are a parent traveling with a frightened child while the cabin crew prepares for an emergency descent.',
    ],
    challenge: {
      id: 'earth-airliner-fire',
      title: 'Fire Outside the Window',
      preferredTags: ['senses', 'structure', 'temperature', 'force', 'flight', 'time', 'leadership'],
      tones: ['tactical', 'moral', 'chaotic'],
      problem:
        'Smoke enters the rear cabin as passengers begin climbing over seats. The crew needs the aisle clear, but something behind the wing is worsening faster than the pilots realize.',
      optionA: 'reveal your ability to assess or contain the physical emergency directly',
      optionB: 'stay inside the cabin and use your limited power to keep hundreds of passengers calm and organized',
    },
    pressures: [
      'You cannot reach the cockpit, every obvious use will be recorded, and landing is still twelve minutes away.',
      'The cabin crew is treating an injured passenger while smoke makes the rear exits increasingly difficult to reach.',
    ],
    stakes: 'A panicked evacuation or one overlooked mechanical failure could turn a survivable emergency into a mass-casualty event.',
  },
  {
    id: 'earth-parent-secret',
    world: 'earth',
    name: 'your family home on a quiet Saturday',
    title: 'What Do You Tell Your Children?',
    atmosphere:
      'Your four children, ages six to fifteen, have noticed too many impossible coincidences. The oldest has begun testing a theory about you, and the youngest only wants you to make the afternoon game more exciting.',
    roles: [
      'You are a parent of four who has hidden an Invested ability from everyone outside the family.',
      'You are the other parent, recently trusted with the truth and worried about what the children can keep secret.',
    ],
    challenge: {
      id: 'earth-family-disclosure',
      title: 'What Do You Tell Your Children?',
      preferredTags: ['emotion', 'connection', 'identity', 'illusion', 'leadership', 'communication'],
      tones: ['moral', 'balanced'],
      problem:
        'Keeping the secret now requires lying directly to your children, but showing them the truth asks each child to carry a dangerous secret they did not choose.',
      optionA: 'tell them and let your ability become part of family life, rules, and play',
      optionB: 'protect them through secrecy until they are older, even if it damages their trust now',
    },
    pressures: [
      'One child will feel betrayed by either choice, and another is likely to demonstrate what they saw to a friend.',
      'The oldest child has already recorded one impossible event and wants an explanation before deciding whether to delete it.',
    ],
    stakes: 'This decision will shape how your children understand honesty, safety, and the responsibility that comes with power.',
  },
  {
    id: 'earth-wildfire-evacuation',
    world: 'earth',
    name: 'a neighborhood under wildfire evacuation',
    title: 'One Road Out',
    atmosphere:
      'Wind pushes embers over a suburban ridge as stalled traffic blocks the only paved evacuation route. Several elderly residents have not answered their phones.',
    roles: [
      'You are a volunteer neighborhood coordinator with family waiting in a car near the evacuation route.',
      'You are an off-duty paramedic who knows which homes contain residents with limited mobility.',
    ],
    challenge: {
      id: 'earth-wildfire-choice',
      title: 'One Road Out',
      preferredTags: ['speed', 'temperature', 'strength', 'breath', 'rescue', 'leadership'],
      tones: ['tactical', 'moral', 'chaotic'],
      problem:
        'Clearing an abandoned truck could reopen the road for hundreds, but checking the unanswered homes will consume the same narrow window before the fire crosses the ridge.',
      optionA: 'spend your reserve opening the evacuation route',
      optionB: 'leave the traffic jam behind and search the homes most likely to contain trapped people',
    },
    pressures: [
      'Smoke is cutting visibility, public safety radio channels are overloaded, and your own family refuses to leave without you.',
      'A fallen power line blocks the nearest homes while police prepare to close the evacuation road entirely.',
    ],
    stakes: 'Every minute commits you to helping one group while the other loses its remaining margin of safety.',
  },
  {
    id: 'earth-hospital-blackout',
    world: 'earth',
    name: 'a regional hospital during a blackout',
    title: 'The Generators Do Not Start',
    atmosphere:
      'A regional power failure darkens the hospital. Emergency lighting activates, but the backup generators fail and critical equipment begins switching to short-lived batteries.',
    roles: [
      'You are an emergency-department nurse who knows the building and the patients at greatest immediate risk.',
      'You are a hospital facilities technician who knows why the generators failed but cannot reach both damaged systems.',
    ],
    challenge: {
      id: 'earth-hospital-blackout-choice',
      title: 'The Generators Do Not Start',
      preferredTags: ['healing', 'speed', 'intellect', 'metal', 'structure', 'leadership'],
      tones: ['tactical', 'moral'],
      problem:
        'The neonatal unit needs immediate power while a stalled elevator contains a surgical team and a patient whose portable ventilator battery is nearly empty.',
      optionA: 'use your ability to keep the neonatal unit functioning',
      optionB: 'reach the elevator and free the trapped surgical team before the ventilator fails',
    },
    pressures: [
      'Your reserve cannot solve both failures, and explaining how you intervened may cost your career and freedom.',
      'The neonatal batteries and the trapped patient’s ventilator now show the same remaining runtime.',
    ],
    stakes: 'Clinical triage has become a personal choice that the official emergency plan never anticipated.',
  },
  {
    id: 'earth-subway-platform',
    world: 'earth',
    name: 'a crowded underground train platform',
    title: 'Thirty Seconds to the Platform',
    atmosphere:
      'A power fault fills the tunnel with smoke as a driverless maintenance car rolls toward a platform crowded by delayed commuters.',
    roles: [
      'You are a daily commuter standing near the emergency intercom when the warning begins.',
      'You are a transit employee who knows the platform evacuation route is too narrow for the crowd.',
    ],
    challenge: {
      id: 'earth-subway-runaway',
      title: 'Thirty Seconds to the Platform',
      preferredTags: ['force', 'speed', 'emotion', 'structure', 'time', 'rescue'],
      tones: ['tactical', 'chaotic'],
      problem:
        'You can act on the moving car itself or on the crowd, but attempting both will divide your attention and exhaust your reserve before either task is complete.',
      optionA: 'commit your power to slowing or diverting the maintenance car',
      optionB: 'move and control the crowd before the car reaches the platform',
    },
    pressures: [
      'Phones are already recording, smoke obscures the tunnel, and the first official warning gives people the wrong direction.',
      'The emergency stairs can handle the crowd or the passengers from an arriving train, but station control has not stopped that train.',
    ],
    stakes: 'A technically successful intervention can still kill people if the crowd reacts badly.',
  },
  {
    id: 'earth-flooded-school-bus',
    world: 'earth',
    name: 'a flooded rural road beside a school bus',
    title: 'The Current Takes the Bus',
    atmosphere:
      'Flash water has lifted a school bus sideways against a guardrail. The driver is injured, children are breaking windows, and the road surface is eroding under the rear wheels.',
    roles: [
      'You are a parent who arrived before emergency crews and recognize several children inside.',
      'You are a volunteer firefighter carrying only basic water-rescue equipment.',
    ],
    challenge: {
      id: 'earth-school-bus-rescue',
      title: 'The Current Takes the Bus',
      preferredTags: ['strength', 'mass', 'breath', 'speed', 'structure', 'rescue'],
      tones: ['tactical', 'moral', 'chaotic'],
      problem:
        'Stabilizing the bus may consume your entire reserve, but entering immediately means choosing which children to bring out before the guardrail fails.',
      optionA: 'use everything to anchor the bus and wait for organized rescue',
      optionB: 'enter the current now and begin extracting children one at a time',
    },
    pressures: [
      'Cold water is rising inside, frightened children are moving the center of mass, and professional rescue is at least eight minutes away.',
      'The guardrail has begun to peel away while the oldest children try to carry younger students toward the rear exit.',
    ],
    stakes: 'The safest plan for the group may be too slow for the children already underwater.',
  },
  {
    id: 'earth-public-exposure',
    world: 'earth',
    name: 'a live televised city council meeting',
    title: 'The Camera Never Cuts Away',
    atmosphere:
      'A contentious public meeting is streaming live when an armed attendee blocks the chamber doors and accuses officials of concealing a deadly environmental report.',
    roles: [
      'You are a local reporter who has spent years building trust with both the officials and the whistleblower.',
      'You are a city employee who privately knows that part of the accusation is true.',
    ],
    challenge: {
      id: 'earth-council-hostage',
      title: 'The Camera Never Cuts Away',
      preferredTags: ['emotion', 'connection', 'illusion', 'speed', 'restraint', 'communication'],
      tones: ['moral', 'tactical'],
      problem:
        'Your ability could end the immediate threat, but doing so on camera may let the officials dismiss the evidence as an Invested manipulation.',
      optionA: 'use your power openly to protect everyone in the room',
      optionB: 'keep talking and preserve the whistleblower\'s credibility while the danger continues',
    },
    pressures: [
      'Police are preparing to enter, the livestream audience is growing, and one frightened official is about to make a reckless move.',
      'The armed attendee offers to surrender to you alone if the environmental report is released before police enter.',
    ],
    stakes: 'Saving the room and exposing the truth may require mutually incompatible choices.',
  },
];
